/**
 * Shared HTTP primitive for the JSON API. Every feature's data layer calls `request<T>`, and `ApiError`
 * is the single error type thrown on any non-2xx — it carries the parsed problem+json `code`/`field`
 * so UIs can branch on machine-readable outcomes.
 *
 * These lived as a byte-identical per-feature copy (one `request` per feature's api.ts) for a
 * while. That meant a fix had to be applied N times, and one copy silently drifted to having no
 * empty-body guard at all — so they're consolidated here: one helper, one place to change. Read-only
 * fetchers that don't fit this shape (the paged GETs in `invoices/api.ts`, blob downloads) still throw
 * `ApiError` directly rather than routing through `request`.
 */

export class ApiError extends Error {
  /** Machine-readable error code from the problem+json body (e.g. `EMAIL_TAKEN`), if present. */
  readonly code?: string;
  /** The request field the error concerns (e.g. `email`), if the server named one. */
  readonly field?: string;

  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
    const problem = tryParseProblem(message);
    this.code = typeof problem?.code === 'string' ? problem.code : undefined;
    this.field = typeof problem?.field === 'string' ? problem.field : undefined;
  }
}

function tryParseProblem(body: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(body);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Throws `ApiError` on any non-2xx so TanStack Query's error state is meaningful; returns the parsed
 * JSON body on 2xx. Body-less success (204, or a body-less 202/201) resolves to `undefined`: the body
 * is read as text and parsed only when non-empty, which sidesteps the `res.json()` SyntaxError trap on
 * an empty response (the bug that a per-feature `204`-only guard used to hit on 202 Accepted).
 */
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, body || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
